import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook for Google Analytics GA4 tracking
 * Provides functions to track events and automatically tracks page views
 */
export const useGoogleAnalytics = () => {
  // Safely get location, handling cases where hook is used outside Router context
  let location = null;
  try {
    location = useLocation();
  } catch (error) {
    // Hook is being used outside Router context, location tracking will be disabled
    console.warn('useGoogleAnalytics: useLocation() failed - hook may be used outside Router context');
  }

  // Track page views automatically when location changes
  useEffect(() => {
    if (typeof window !== 'undefined' && window.gtag && location) {
      window.gtag('config', 'G-2E788B0R1S', {
        page_path: location.pathname + location.search,
        page_title: document.title
      });
    }
  }, [location]);

  // Track custom events
  const trackEvent = useCallback((eventName, parameters = {}) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, {
        event_category: 'user_interaction',
        ...parameters
      });
    }
  }, []);

  // Track page views manually
  const trackPageView = useCallback((pageTitle, pagePath) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', 'G-2E788B0R1S', {
        page_title: pageTitle,
        page_path: pagePath
      });
    }
  }, []);

  // Track user login
  const trackLogin = useCallback((method = 'email') => {
    trackEvent('login', {
      method: method,
      event_category: 'authentication'
    });
  }, [trackEvent]);

  // Track user logout
  const trackLogout = useCallback(() => {
    trackEvent('logout', {
      event_category: 'authentication'
    });
  }, [trackEvent]);

  // Track user registration
  const trackSignUp = useCallback((method = 'email') => {
    trackEvent('sign_up', {
      method: method,
      event_category: 'authentication'
    });
  }, [trackEvent]);

  // Track feature usage
  const trackFeatureUsage = useCallback((featureName, action = 'used') => {
    trackEvent('feature_usage', {
      feature_name: featureName,
      action: action,
      event_category: 'feature_interaction'
    });
  }, [trackEvent]);

  // Track error events
  const trackError = useCallback((errorType, errorMessage, errorCode = null) => {
    trackEvent('error', {
      error_type: errorType,
      error_message: errorMessage,
      error_code: errorCode,
      event_category: 'error'
    });
  }, [trackEvent]);

  // Track email actions
  const trackEmailAction = useCallback((action, recipientCount = 1) => {
    trackEvent('email_action', {
      action: action,
      recipient_count: recipientCount,
      event_category: 'communication'
    });
  }, [trackEvent]);

  // Track user management actions
  const trackUserManagement = useCallback((action, userType = 'regular') => {
    trackEvent('user_management', {
      action: action,
      user_type: userType,
      event_category: 'administration'
    });
  }, [trackEvent]);

  // Track content management actions
  const trackContentManagement = useCallback((action, contentType) => {
    trackEvent('content_management', {
      action: action,
      content_type: contentType,
      event_category: 'content'
    });
  }, [trackEvent]);

  // Track device management actions
  const trackDeviceManagement = useCallback((action, deviceCount = 1) => {
    trackEvent('device_management', {
      action: action,
      device_count: deviceCount,
      event_category: 'device'
    });
  }, [trackEvent]);

  // Track subscription actions
  const trackSubscriptionAction = useCallback((action, planType = null) => {
    trackEvent('subscription_action', {
      action: action,
      plan_type: planType,
      event_category: 'subscription'
    });
  }, [trackEvent]);

  // Track AI feature usage
  const trackAIFeature = useCallback((featureName, action = 'used') => {
    trackEvent('ai_feature', {
      feature_name: featureName,
      action: action,
      event_category: 'ai_interaction'
    });
  }, [trackEvent]);

  // Track analytics page views
  const trackAnalyticsView = useCallback((analyticsType = 'general') => {
    trackEvent('analytics_view', {
      analytics_type: analyticsType,
      event_category: 'analytics'
    });
  }, [trackEvent]);

  // Track settings changes
  const trackSettingsChange = useCallback((settingType, settingValue) => {
    trackEvent('settings_change', {
      setting_type: settingType,
      setting_value: settingValue,
      event_category: 'settings'
    });
  }, [trackEvent]);

  return {
    trackEvent,
    trackPageView,
    trackLogin,
    trackLogout,
    trackSignUp,
    trackFeatureUsage,
    trackError,
    trackEmailAction,
    trackUserManagement,
    trackContentManagement,
    trackDeviceManagement,
    trackSubscriptionAction,
    trackAIFeature,
    trackAnalyticsView,
    trackSettingsChange,
    // Helper to check if location tracking is available
    isLocationTrackingAvailable: !!location
  };
};

export default useGoogleAnalytics; 